/**
 * Where the auth provider lives, decided at run time rather than build time.
 *
 * The portal ships as one static bundle behind nginx, and the same image is
 * meant to run in every environment — baking a tenant URL into it at `vite
 * build` would make the image environment-specific and mean a rebuild to point
 * it somewhere else. So nginx writes this into the page from its own
 * environment, exactly as it already does for the API upstream.
 *
 * Absent means no auth provider is configured and the app runs open, which is
 * how `docker compose up` and a laptop keep working with no PropelAuth tenant.
 * The API decides the same question independently from its own environment;
 * neither side trusts the other for it.
 */
declare global {
  interface Window {
    VEYSO_CONFIG?: {
      propelAuthUrl?: string;
      authMethods?: string;
      posthogKey?: string;
      posthogHost?: string;
    };
  }
}

export function propelAuthUrl(): string | null {
  const url = window.VEYSO_CONFIG?.propelAuthUrl?.trim();
  // A template that nobody substituted leaves the literal `${...}` behind, and
  // handing that to the SDK produces a confusing error a long way from here.
  if (!url || url.startsWith("${")) return null;
  return url;
}

export function authEnabled(): boolean {
  return propelAuthUrl() !== null;
}

/**
 * Which sign-in methods to offer.
 *
 * Told to the page rather than discovered, because it cannot be discovered:
 * PropelAuth publishes no endpoint that reports which methods a tenant allows.
 * The only way to find out from a browser is to attempt one and read the
 * refusal, which is a poor thing to do to somebody who has just typed their
 * address in.
 *
 * So it is configuration, and it has to be kept in step with the dashboard by
 * hand — turning magic link off there and leaving it on here offers a button
 * that answers "Not found". The default is both, which is right for a tenant
 * nobody has configured this for: a method that is offered and refused is
 * recoverable, and a method that works but is not offered may be the only one
 * there is.
 *
 * `VEYSO_AUTH_METHODS=password` on the web app narrows it.
 */
export type AuthMethod = "password" | "link";

export function enabledAuthMethods(): AuthMethod[] {
  const raw = window.VEYSO_CONFIG?.authMethods?.trim();
  if (!raw || raw.startsWith("${")) return ["password", "link"];

  const asked = raw
    .split(",")
    .map((entry) => entry.trim().toLowerCase())
    .filter((entry): entry is AuthMethod => entry === "password" || entry === "link");

  // An unreadable setting falls back to both rather than to none. A typo should
  // not be able to produce a sign-in screen with no way to sign in.
  return asked.length > 0 ? asked : ["password", "link"];
}

/**
 * Where analytics reports to, if anywhere.
 *
 * Here for the same reason the auth tenant is: the portal ships as one static
 * image, and a key compiled in at build time would make that image belong to
 * one environment. `@posthog/wizard` wired it to `import.meta.env`, which does
 * exactly that; this is the correction.
 *
 * Null when either half is missing, because half a configuration is not a
 * working one and a client pointed at the wrong host fails quietly.
 */
export function analyticsConfig(): { key: string; host: string } | null {
  const usable = (value: string | undefined) => {
    const trimmed = value?.trim();
    // envsubst leaves the literal `${...}` behind for anything outside its
    // filter, and that is not a key.
    return trimmed && !trimmed.startsWith("${") ? trimmed : null;
  };

  const key = usable(window.VEYSO_CONFIG?.posthogKey);
  const host = usable(window.VEYSO_CONFIG?.posthogHost);
  return key && host ? { key, host } : null;
}
